import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import TuneIcon from '@material-ui/icons/Tune';
import SearchIcon from '@material-ui/icons/Search';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { previewMerchandisingQueryMatch } from '../state/merchandisingSlice';
import {
  SegmentedControl,
  SelectionCheckbox,
} from './MerchandisingWorkbenchUi';
import AdvancedRuleSelect from './AdvancedRuleSelect';
import SelectionDialog, { createSelectionSource } from './SelectionDialog';
import { UnsavedChangesDialog } from './UnsavedChangesGuard';
import { selectionDraftValid } from '../model/selectionDraftValidation';
import { usePreviewOpener } from '../../shell/preview/usePreviewOpener';
import { normalizeShopUrl } from '../../../util/previewHelper';

const copy = (t, key, values) =>
  t(`merchandising.advancedPrototype.${key}`, values);
const clone = value => JSON.parse(JSON.stringify(value));
const lines = value =>
  value
    .split('\n')
    .map(entry => entry.trim())
    .filter(Boolean);
export const safeResponseLink = url =>
  typeof url === 'string' &&
  /^\/(?!\/)/.test(url) &&
  Array.from(url).every(
    character => character.charCodeAt(0) > 32 && character !== '\\'
  ) &&
  !/%(?:2f|5c|0[0-9a-f]|1[0-9a-f]|20)/i.test(url);
export const responseDraftValid = policy => {
  if (!policy) return true;
  if (policy.mode === 'empty')
    return (
      typeof policy.message === 'string' &&
      Boolean(policy.message.trim()) &&
      policy.message.length <= 1000 &&
      (!policy.link ||
        (policy.link.label.trim() && safeResponseLink(policy.link.url)))
    );
  return policy.mode === 'selection' && selectionDraftValid(policy);
};

export const QueryMatchControl = ({
  model,
  persistedMatchConfig,
  support,
  disabled,
  onChange,
}) => {
  const t = useLocalizedMessage();
  const dispatch = useDispatch();
  const intl = useIntl();
  const localeName = locale => {
    try {
      return typeof Intl.DisplayNames === 'function'
        ? new Intl.DisplayNames([intl.locale], { type: 'language' }).of(locale)
        : locale;
    } catch (_) {
      return locale;
    }
  };
  const [open, setOpen] = useState(false);
  const [discard, setDiscard] = useState(false);
  const [baseline, setBaseline] = useState('');
  useEffect(() => {
    if (disabled || !support.matchPreview) {
      setOpen(false);
      setDiscard(false);
    }
  }, [disabled, support.matchPreview]);
  const initial = () =>
    clone(
      model.matchConfig || {
        version: 'query-match.v1',
        mode: 'exact',
        locale: support.locale,
        aliases: [],
        exclusions: [],
      }
    );
  const [draft, setDraft] = useState(initial);
  const [aliases, setAliases] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const generation = useRef(0);
  const config = {
    ...draft,
    aliases: draft.mode === 'exact' ? [] : lines(aliases),
    exclusions: draft.mode === 'exact' ? [] : lines(exclusions),
  };
  const close = () =>
    JSON.stringify(config) === baseline ? setOpen(false) : setDiscard(true);
  const valid =
    config.aliases.length <= 32 &&
    config.exclusions.length <= 32 &&
    config.aliases
      .concat(config.exclusions)
      .every(value => value.length <= 100) &&
    support.locales.includes(config.locale);
  const fingerprint = JSON.stringify({
    config,
    query,
    queries: model.queries,
    languageId: model.languageId,
  });
  useEffect(() => {
    const own = ++generation.current;
    setResult(null);
    setError(null);
    setChecking(Boolean(open && query.trim() && valid));
    if (!open || !query.trim() || !valid) return undefined;
    const timeout = setTimeout(async () => {
      const action = await dispatch(
        previewMerchandisingQueryMatch({
          query: query.trim(),
          queries: model.queries,
          match_config: config,
          language_id: model.languageId,
        })
      );
      if (generation.current !== own) return;
      setChecking(false);
      if (previewMerchandisingQueryMatch.fulfilled.match(action))
        setResult(action.payload);
      else
        setError(action.payload?.message || t('merchandising.error.generic'));
    }, 350);
    return () => {
      clearTimeout(timeout);
      generation.current += 1;
    };
    // Serialized configuration controls request invalidation, including edits and dialog close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, open, fingerprint]);
  const modeLabel = mode =>
    mode === 'concept'
      ? t('merchandising.matching.containsLabel')
      : copy(t, mode === 'forms' ? 'matchForms' : 'matchExact');
  const modeHelp = mode => t(`merchandising.matching.${mode}Help`);
  if (!support.cloudEnabled || (!support.matchPreview && !model.matchConfig))
    return null;
  return (
    <>
      <Button
        className="mp-advanced-match-chip"
        startIcon={<TuneIcon />}
        disabled={disabled}
        onClick={() => {
          const next = initial();
          setDraft(next);
          setBaseline(JSON.stringify(next));
          setDiscard(false);
          setAliases(next.aliases.join('\n'));
          setExclusions(next.exclusions.join('\n'));
          setQuery('');
          setOpen(true);
        }}
      >
        {modeLabel(model.matchConfig?.mode || 'exact')}
      </Button>
      <Dialog
        className="mp-dialog mp-advanced-match-dialog"
        open={open}
        onClose={close}
        maxWidth="sm"
        fullWidth
        aria-labelledby="mp-match-dialog-title"
      >
        <DialogTitle id="mp-match-dialog-title">
          {copy(t, 'matchDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <p className="mp-advanced-dialog-intro">
            {t('merchandising.matching.phrasesHelp')}
          </p>
          <SegmentedControl
            value={draft.mode}
            options={['exact', 'forms', 'concept']
              .filter(mode => mode === 'exact' || support[mode])
              .map(mode => ({ VALUE: mode, LABEL: modeLabel(mode) }))}
            onChange={mode => setDraft({ ...draft, mode })}
            label={copy(t, 'matchModeLabel')}
          />
          <p className="mp-matching-mode-help">{modeHelp(draft.mode)}</p>
          <div
            className="mp-matching-phrases"
            aria-label={t('merchandising.matching.phrasesTitle')}
          >
            {[
              ...new Set([
                ...model.queries.filter(phrase => phrase.trim()),
                ...config.aliases,
              ]),
            ].map(phrase => (
              <span key={phrase}>{phrase}</span>
            ))}
          </div>
          {support.locales.length > 1 && (
            <AdvancedRuleSelect
              label={t('merchandising.advanced.locale')}
              value={draft.locale}
              options={support.locales.map(locale => ({
                value: locale,
                label: localeName(locale),
              }))}
              onChange={locale => setDraft({ ...draft, locale })}
            />
          )}
          {draft.mode !== 'exact' && (
            <details
              className="mp-matching-more"
              open={
                Boolean(
                  persistedMatchConfig?.aliases?.length ||
                    persistedMatchConfig?.exclusions?.length
                ) || undefined
              }
            >
              <summary>{t('merchandising.matching.moreOptions')}</summary>
              <div className="mp-advanced-concept-grid">
                <label className="mp-advanced-field">
                  <span id="mp-match-additional-label">
                    {t('merchandising.matching.additionalPhrases')}
                  </span>
                  <textarea
                    value={aliases}
                    aria-labelledby="mp-match-additional-label"
                    onChange={event => setAliases(event.target.value)}
                    aria-describedby="mp-match-alternatives-help mp-match-limits"
                  />
                  <small id="mp-match-alternatives-help">
                    {t('merchandising.matching.additionalHelp')}
                  </small>
                </label>
                <label className="mp-advanced-field">
                  <span id="mp-match-excluded-label">
                    {t('merchandising.matching.excludedPhrases')}
                  </span>
                  <textarea
                    value={exclusions}
                    aria-labelledby="mp-match-excluded-label"
                    onChange={event => setExclusions(event.target.value)}
                    aria-describedby="mp-match-exclusions-help mp-match-limits"
                  />
                  <small id="mp-match-exclusions-help">
                    {t('merchandising.matching.excludedHelp')}
                  </small>
                </label>
              </div>
              <p id="mp-match-limits">
                {t('merchandising.advanced.phraseLimits')}
              </p>
            </details>
          )}
          <label className="mp-advanced-field mp-advanced-match-test">
            <span>{copy(t, 'testQuery')}</span>
            <input
              maxLength={512}
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          </label>
          <div
            className={`mp-advanced-match-result ${
              result?.matched ? 'is-match' : ''
            }`}
            role="status"
            aria-busy={checking}
          >
            {error ||
              (checking ? (
                t('merchandising.matching.checking')
              ) : result ? (
                <>
                  <strong>
                    {t(
                      `merchandising.matching.${
                        result.excluded
                          ? 'excluded'
                          : result.matched
                          ? 'positive'
                          : 'negative'
                      }`
                    )}
                  </strong>
                  {!result.matched && (
                    <p>
                      {t(
                        `merchandising.matching.${
                          result.excluded
                            ? 'excludedResultHelp'
                            : 'negativeHelp'
                        }`
                      )}
                    </p>
                  )}
                </>
              ) : (
                t('merchandising.matching.testPrompt')
              ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{copy(t, 'cancel')}</Button>
          <Button
            disabled={!valid}
            color="primary"
            onClick={() => {
              onChange(
                config.mode === 'exact'
                  ? persistedMatchConfig
                    ? { ...config, aliases: [], exclusions: [] }
                    : null
                  : config
              );
              setOpen(false);
            }}
          >
            {copy(t, 'done')}
          </Button>
        </DialogActions>
      </Dialog>
      <UnsavedChangesDialog
        guard={{
          open: discard,
          keepEditing: () => setDiscard(false),
          discardAndLeave: () => {
            setDiscard(false);
            setOpen(false);
          },
        }}
      />
    </>
  );
};

export const QueryResponseControl = ({
  model,
  baseline,
  support,
  disabled,
  onChange,
  selectionPreview = null,
}) => {
  const t = useLocalizedMessage();
  const mode = model.responsePolicy?.mode || 'search';
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  useEffect(() => {
    if (disabled) {
      setSelectionOpen(false);
      setPendingMode(null);
    }
  }, [disabled]);
  const drafts = useRef({});
  const applyMode = next => {
    drafts.current[mode] = model.responsePolicy;
    const policy =
      next === 'search'
        ? null
        : drafts.current[next] ||
          (next === 'selection'
            ? {
                version: 'query-response.v1',
                mode: 'selection',
                sources: [createSelectionSource()],
              }
            : { version: 'query-response.v1', mode: 'empty', message: '' });
    onChange(policy);
    setPendingMode(null);
  };
  const changeMode = next => {
    const persistedActions =
      baseline.id &&
      (Object.keys(baseline.productChanges || {}).length ||
        baseline.opaqueActions?.length ||
        baseline.rewrite ||
        Object.values(baseline.groupChanges || {}).some(
          bucket => Object.keys(bucket).length
        ));
    if (
      persistedActions &&
      ((next === 'empty' && mode !== 'empty') ||
        (next === 'selection' &&
          mode === 'search' &&
          (baseline.rewrite ||
            baseline.opaqueActions?.some(
              action => action.type === 'redirect'
            ))))
    )
      setPendingMode(next);
    else applyMode(next);
  };
  if (
    !support.cloudEnabled ||
    (!support.selection && !support.empty && !model.responsePolicy)
  )
    return null;
  return (
    <>
      <section className="mp-setting-card mp-advanced-response-card">
        <div className="mp-inspector-section-heading">
          <div>
            <strong>{copy(t, 'responseTitle')}</strong>
            <span>{copy(t, 'responseHelp')}</span>
          </div>
        </div>
        <SegmentedControl
          value={mode}
          options={['search', 'selection', 'empty']
            .filter(value => value === 'search' || support[value])
            .map(value => ({
              VALUE: value,
              LABEL: copy(
                t,
                {
                  search: 'responseSearch',
                  selection: 'responseSelection',
                  empty: 'responseEmpty',
                }[value]
              ),
            }))}
          onChange={next => {
            if (!disabled) changeMode(next);
          }}
          label={copy(t, 'responseLabel')}
        />
      </section>
      {mode === 'selection' && (
        <section className="mp-setting-card mp-advanced-mode-card">
          <div className="mp-selection-summary-heading">
            <strong>{t('merchandising.response.selectionTitle')}</strong>
            {selectionPreview?.busy && (
              <span role="status">
                {t('merchandising.response.selectionUpdating')}
              </span>
            )}
          </div>
          <p>{t('merchandising.response.selectionHelp')}</p>
          <ul className="mp-selection-source-summaries">
            {(model.responsePolicy.sources || []).map((source, index) => (
              <li key={source.id}>
                <span>
                  {t('merchandising.advancedPrototype.group', {
                    number: index + 1,
                  })}
                </span>
                <strong>
                  {source.query?.trim() ||
                    t('merchandising.advanced.allCatalog')}
                </strong>
              </li>
            ))}
          </ul>
          <Button
            disabled={disabled}
            variant="outlined"
            onClick={() => setSelectionOpen(true)}
          >
            {copy(t, 'editSelection')}
          </Button>
        </section>
      )}
      {mode === 'empty' && (
        <section className="mp-setting-card mp-advanced-mode-card">
          <label className="mp-advanced-field">
            <span>{copy(t, 'emptyMessage')}</span>
            <textarea
              maxLength={1000}
              disabled={disabled}
              value={model.responsePolicy.message || ''}
              onChange={event =>
                onChange({
                  ...model.responsePolicy,
                  message: event.target.value,
                })
              }
            />
          </label>
          {support.destination && (
            <label className="mp-checkbox-label">
              <SelectionCheckbox
                label={copy(t, 'emptyLinkToggle')}
                disabled={disabled}
                checked={Boolean(model.responsePolicy.link)}
                onChange={event => {
                  const { link, ...policy } = model.responsePolicy;
                  onChange(
                    event.target.checked
                      ? { ...policy, link: { label: '', url: '' } }
                      : policy
                  );
                }}
              />
              <span>{copy(t, 'emptyLinkToggle')}</span>
            </label>
          )}
          {model.responsePolicy.link && (
            <div className="mp-advanced-link-fields">
              {['label', 'url'].map(field => (
                <label className="mp-advanced-field" key={field}>
                  <span>
                    {copy(
                      t,
                      field === 'label' ? 'emptyLinkLabel' : 'emptyLinkUrl'
                    )}
                  </span>
                  <input
                    disabled={disabled}
                    value={model.responsePolicy.link[field]}
                    onChange={event =>
                      onChange({
                        ...model.responsePolicy,
                        link: {
                          ...model.responsePolicy.link,
                          [field]: event.target.value,
                        },
                      })
                    }
                  />
                </label>
              ))}
            </div>
          )}
        </section>
      )}
      {mode === 'selection' && (
        <SelectionDialog
          policy={model.responsePolicy}
          valid={responseDraftValid}
          support={support}
          languageId={model.languageId}
          open={selectionOpen}
          onClose={() => setSelectionOpen(false)}
          onSave={policy => {
            onChange(policy);
            setSelectionOpen(false);
          }}
        />
      )}
      <Dialog
        className="mp-dialog"
        open={Boolean(pendingMode)}
        onClose={() => setPendingMode(null)}
        aria-labelledby="mp-response-confirm-title"
      >
        <DialogTitle id="mp-response-confirm-title">
          {t('merchandising.advanced.replaceActionsTitle')}
        </DialogTitle>
        <DialogContent>
          {t('merchandising.advanced.replaceActionsBody')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingMode(null)}>
            {copy(t, 'cancel')}
          </Button>
          <Button onClick={() => applyMode(pendingMode)}>
            {t('merchandising.advanced.replaceActions')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const QueryResponsePreview = ({ response }) => {
  const t = useLocalizedMessage();
  const { previewShopAddress } = usePreviewOpener();
  let previewHref = null;
  if (response.link && safeResponseLink(response.link.url) && previewShopAddress) {
    try {
      const storefront = new URL(normalizeShopUrl(previewShopAddress));
      if (['http:', 'https:'].includes(storefront.protocol) && !storefront.username && !storefront.password) {
        previewHref = new URL(response.link.url, storefront).href;
      }
    } catch (_) {
      // A preview without a configured storefront must not navigate on the admin host.
    }
  }
  return (
    <div
      className="mp-advanced-empty-preview"
      role="status"
      aria-label={t('merchandising.advanced.noResults')}
    >
      <span aria-hidden="true">
        <SearchIcon />
      </span>
      <h2>{t('merchandising.advanced.noResults')}</h2>
      <p>
        {response.message || t('merchandising.advanced.emptyPreviewMessage')}
      </p>
      {response.link && safeResponseLink(response.link.url) && (
        <Button
          component={previewHref ? 'a' : 'button'}
          href={previewHref || undefined}
          disabled={!previewHref}
          target={previewHref ? '_blank' : undefined}
          rel={previewHref ? 'noopener noreferrer' : undefined}
          variant="outlined"
        >
          {response.link.label}
        </Button>
      )}
    </div>
  );
};

export const QueryResponseChange = ({ response }) => {
  const t = useLocalizedMessage();
  return (
    <article className="mp-change-review-card is-setting">
      <div>
        <strong>
          {copy(
            t,
            response.mode === 'empty' ? 'responseEmpty' : 'responseSelection'
          )}
        </strong>
        <span>
          {response.mode === 'empty'
            ? response.message
            : copy(t, 'selectionGroups', {
                count: response.sources?.length || 0,
              })}
        </span>
      </div>
    </article>
  );
};
